import * as React from "react";
import { useTranslation } from "react-i18next";

import { PERSONA_SCRIPT_IMPORT_MAX_BYTES } from "@nexus/contracts";
import { Button, Icon, cn } from "@nexus/ui";

const ACCEPT =
  ".pdf,.docx,.txt,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const ZONE_CLASS = cn(
  "flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-input-border bg-muted/20 px-4 py-5 text-center",
  "transition-[border-color,background-color] duration-200 motion-reduce:transition-none",
);

export interface PersonaImportFileZoneProps {
  file: File | null;
  isExtracting: boolean;
  disabled?: boolean;
  onFiles: (files: FileList | File[]) => void;
}

function formatImportFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) {
    const kb = bytes / 1024;
    return `${kb < 10 ? kb.toFixed(1) : String(Math.round(kb))} KB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : String(Math.round(mb))} MB`;
}

function ExtractProgressBar({ label }: { label: string }) {
  return (
    <div
      className="bg-muted h-1 w-full overflow-hidden rounded-full"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="bg-primary h-full w-2/5 rounded-full motion-safe:animate-pulse motion-reduce:opacity-70" />
    </div>
  );
}

export function PersonaImportFileZone({
  file,
  isExtracting,
  disabled = false,
  onFiles,
}: PersonaImportFileZoneProps) {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) onFiles(files);
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (disabled || isExtracting) return;
    if (event.dataTransfer.files.length > 0) onFiles(event.dataTransfer.files);
  };

  const fileInput = (hidden: boolean) => (
    <input
      ref={inputRef}
      type="file"
      data-testid="persona-import-file"
      accept={ACCEPT}
      disabled={disabled || isExtracting}
      className="sr-only"
      tabIndex={hidden ? -1 : undefined}
      aria-hidden={hidden || undefined}
      onChange={handleInputChange}
    />
  );

  if (file) {
    const size = formatImportFileSize(file.size);
    const status = isExtracting
      ? t("personaOpportunities.scripts.import.fileReading", "Reading…")
      : t("personaOpportunities.scripts.import.fileReady", "Ready to review");
    return (
      <div
        className={cn(
          "flex flex-col gap-2 rounded-md border px-3 py-2.5",
          isExtracting ? "border-primary/30 bg-primary/5" : "border-border bg-card",
        )}
        data-testid="persona-import-file-row"
      >
        <div className="flex items-start gap-3">
          <span className="border-border bg-background flex size-9 shrink-0 items-center justify-center rounded-md border">
            {isExtracting ? (
              <Icon
                name="loader"
                size="sm"
                className="motion-safe:animate-spin motion-reduce:animate-none"
              />
            ) : (
              <Icon name="file" size="sm" />
            )}
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <p className="truncate text-sm font-medium text-foreground" title={file.name}>
              {file.name}
            </p>
            <p role="status" className="mt-0.5 truncate text-xs text-muted-foreground">
              {`${size} · ${status}`}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-11 shrink-0 px-2 [@media(hover:hover)_and_(pointer:fine)]:min-h-8"
            disabled={disabled || isExtracting}
            onClick={() => inputRef.current?.click()}
          >
            {t("personaOpportunities.scripts.import.dropzoneReplace", "Replace")}
          </Button>
        </div>
        {isExtracting ? (
          <ExtractProgressBar
            label={t("personaOpportunities.scripts.import.extracting", "Reading file…")}
          />
        ) : null}
        {fileInput(true)}
      </div>
    );
  }

  const content = (
    <>
      <span className="border-border bg-background flex size-9 items-center justify-center rounded-md border">
        <Icon name="upload" size="sm" className="text-muted-foreground" />
      </span>
      <div className="flex flex-col gap-0.5">
        <p className="text-sm font-medium text-foreground">
          {t("personaOpportunities.scripts.import.dropzoneTitle", "Drop a file here, or browse")}
        </p>
        <p className="text-xs text-muted-foreground">
          {t("personaOpportunities.scripts.import.dropzoneConstraints", {
            maxMb: PERSONA_SCRIPT_IMPORT_MAX_BYTES / (1024 * 1024),
            defaultValue: "1 file · PDF, DOCX, or TXT · Max {{maxMb}} MB",
          })}
        </p>
      </div>
      <span className="inline-flex h-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background px-2.5 text-xs text-foreground">
        {t("personaOpportunities.scripts.import.dropzoneBrowse", "Choose file")}
      </span>
      {fileInput(false)}
    </>
  );

  if (disabled) {
    return (
      <div
        aria-label={t(
          "personaOpportunities.scripts.import.dropzoneLabel",
          "Upload a script file — drag and drop or browse",
        )}
        aria-disabled="true"
        className={cn(ZONE_CLASS, "opacity-50")}
      >
        {content}
      </div>
    );
  }

  return (
    <label
      aria-label={t(
        "personaOpportunities.scripts.import.dropzoneLabel",
        "Upload a script file — drag and drop or browse",
      )}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={cn(
        ZONE_CLASS,
        "group cursor-pointer hover:border-primary/25 hover:bg-muted/30",
        isDragging && "border-primary bg-primary-subtle/60",
      )}
    >
      {content}
    </label>
  );
}
